import { useTranslation } from 'react-i18next';
import { cn } from '@/lib';
import { useEmployeeList } from '../../hooks/useEmployeeList';
import { useGlobalUserRoles } from '../../hooks';
import { useMemo } from 'react';
import type { Employee } from '../../types';

interface SelectExistingContactProps {
    companyId: string;
    selectedContactId: string | null;
    onSelect: (contactId: string | null) => void;
}

export function SelectExistingContact({
    companyId,
    selectedContactId,
    onSelect,
}: SelectExistingContactProps) {
    const { t } = useTranslation();
    const { employees, isLoading, searchInput, setSearchInput } = useEmployeeList({
        companyId,
        initialPageSize: 50,
    });
    const { data: roles } = useGlobalUserRoles();

    const roleMap = useMemo(() => {
        if (!roles) return new Map<string, string>();
        return new Map(roles.map((role) => [role.id, role.name]));
    }, [roles]);

    const contactEmployees = useMemo(
        () => employees.filter((emp) => !!emp.contact),
        [employees]
    );

    const handleSelect = (employee: Employee) => {
        const contactId = employee.contact?.id;
        if (!contactId) return;
        onSelect(selectedContactId === contactId ? null : contactId);
    };

    return (
        <div className="space-y-3">
            <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('employees.createForm.selectContact.searchPlaceholder')}
                className="w-full rounded-sm border border-border bg-surface px-3 py-2 text-sm text-onsurface placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />

            {isLoading && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin inline-block size-6 border-2 border-current border-t-transparent rounded-full text-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">
                        {t('employees.createForm.selectContact.loading')}
                    </span>
                </div>
            )}

            {!isLoading && contactEmployees.length === 0 && (
                <div className="flex items-center justify-center py-8">
                    <p className="text-sm text-muted-foreground">
                        {t('employees.createForm.selectContact.noResults')}
                    </p>
                </div>
            )}

            {!isLoading && contactEmployees.length > 0 && (
                <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-1">
                    {contactEmployees.map((employee) => {
                        const contactId = employee.contact?.id;
                        const isSelected = contactId === selectedContactId;
                        const name = employee.contact?.name || '-';
                        const email = employee.contact?.email || employee.user?.userEmail || '-';
                        const roleName = employee.contact?.role
                            ? roleMap.get(employee.contact.role) || employee.contact.role
                            : '-';

                        return (
                            <button
                                key={contactId}
                                type="button"
                                onClick={() => handleSelect(employee)}
                                className={cn(
                                    'flex items-center gap-3 w-full text-left rounded-lg border p-3 transition-all',
                                    isSelected
                                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                        : 'border-border bg-surface hover:border-primary/50 hover:bg-surface-darker'
                                )}
                            >
                                <div
                                    className={cn(
                                        'size-4 shrink-0 rounded-full border-2 flex items-center justify-center',
                                        isSelected ? 'border-primary' : 'border-muted'
                                    )}
                                >
                                    {isSelected && (
                                        <div className="size-2 rounded-full bg-primary" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p
                                        className={cn(
                                            'text-sm font-medium truncate',
                                            isSelected ? 'text-primary' : 'text-onsurface'
                                        )}
                                    >
                                        {name}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {email}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {roleName}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
